import { useCallback, useMemo } from 'react';

import { useActorRef, useSelector } from '@xstate/react';
import { fromPromise } from 'xstate';

import { scanToPayMachine } from './scan-to-pay.machine';
import {
  type FetchDataResponse,
  type FetchIdentifyTokenResponse,
  type FetchPaymentTokenResponse,
  type FetchCreditsResponse,
} from './scan-to-pay.machine.actors';

import type { ScanToPayContext } from './scan-to-pay.machine.context';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the Scan to Pay state machine.
 */
export const useScanToPayMachine = (params: UseScanToPayMachineParams) => {
  const { fetchData, fetchIdentifyToken, fetchCredits, fetchPaymentToken } = params;

  // ─── Machine ─────────────────────────────────────────────────────────

  const actors = useMemo(
    () => ({
      fetchData: fromPromise<FetchDataResponse>(async () => fetchData()),
      fetchCredits: fromPromise<FetchCreditsResponse>(async () => fetchCredits()),
      fetchIdentifyToken: fromPromise<FetchIdentifyTokenResponse>(async () => fetchIdentifyToken()),
      fetchPaymentToken: fromPromise<FetchPaymentTokenResponse, ScanToPayContext>(
        async ({ input }) => fetchPaymentToken(input),
      ),
    }),
    [fetchData, fetchCredits, fetchIdentifyToken, fetchPaymentToken],
  );

  const machine = useMemo(() => scanToPayMachine.provide({ actors }), [actors]);
  const actorRef = useActorRef(machine);

  // ─── Context ─────────────────────────────────────────────────────────

  const context = useSelector(actorRef, (snapshot) => snapshot.context);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isScanToPayFailed = useSelector(actorRef, (snapshot) => snapshot.matches('failed'));

  const isFetchingData = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.matches('fetching') ||
      snapshot.matches({ ready: { paymentToken: 'fetching' } }) ||
      snapshot.matches({ ready: { identifyToken: 'fetching' } }),
  );

  const isShowingLoyaltyInfo = useSelector(actorRef, (snapshot) =>
    snapshot.matches({ ready: { standard: 'loyaltyInfo' } }),
  );

  const isShowingFaq = useSelector(actorRef, (snapshot) =>
    snapshot.matches({ ready: { standard: 'faq' } }),
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const start = useCallback(() => {
    actorRef.send({ type: 'START' });
  }, [actorRef]);

  const cancelRequest = useCallback(() => {
    actorRef.send({ type: 'CANCEL_REQUEST' });
  }, [actorRef]);

  const switchUseCredit = useCallback(() => {
    actorRef.send({ type: 'SWITCH_USE_CREDIT' });
  }, [actorRef]);

  const requestLoyaltyInfo = useCallback(() => {
    actorRef.send({ type: 'REQUEST_LOYALTY_INFO' });
  }, [actorRef]);

  const refetchPaymentToken = useCallback(() => {
    actorRef.send({ type: 'REFETCH_PAYMENT_TOKEN' });
  }, [actorRef]);

  const refetchIdentifyToken = useCallback(() => {
    actorRef.send({ type: 'REFETCH_IDENTIFY_TOKEN' });
  }, [actorRef]);

  const refetchCredits = useCallback(() => {
    actorRef.send({ type: 'REFETCH_CREDITS' });
  }, [actorRef]);

  const requestFaq = useCallback(() => {
    actorRef.send({ type: 'REQUEST_FAQ' });
  }, [actorRef]);

  // ─── Response ────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,
      isScanToPayFailed,
      isFetchingData,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    }),
    [
      context,
      isScanToPayFailed,
      isFetchingData,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScanToPayMachineParams = {
  fetchData: () => Promise<FetchDataResponse>;
  fetchIdentifyToken: () => Promise<FetchIdentifyTokenResponse>;
  fetchPaymentToken: (context: ScanToPayContext) => Promise<FetchPaymentTokenResponse>;
  fetchCredits: () => Promise<FetchCreditsResponse>;
};
